import { IUser } from "providers/auth/types"
import {
  useMutation,
  UseQueryOptions,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query"
import api from "services/api"

export interface INotification {
  id: string
  link: string
  avatar: string
  creator: IUser
  subject: string
  time: string
  module: string
  details: string
  viewed: boolean
  type: string
}

interface IResponse extends IPaginatedResponse<INotification[]> {
  total_unread: number
}

export function useNotifications(config?: UseQueryOptions<any>) {
  return useInfiniteQuery<IResponse>(
    ["suite", "communication", "notifications"],
    async ({ pageParam = 1 }) =>
      api
        .get(`suite/communication/notifications`, {
          params: {
            per_page: 10,
            page: pageParam,
            paginated: true,
          },
        })
        .then(({ data }) => data),
    {
      refetchOnWindowFocus: false,
      getNextPageParam: ({ meta }) => {
        return meta?.last_page > meta?.current_page && meta?.current_page + 1
      },
      ...config,
    }
  )
}

export function useUnreadNotifications(config?: UseQueryOptions<any>) {
  return useInfiniteQuery<IResponse>(
    ["suite", "communication", "notifications", "unread"],
    async ({ pageParam = 1 }) =>
      api
        .get(`suite/communication/notifications`, {
          params: {
            per_page: 10,
            page: pageParam,
            paginated: true,
            filter: { unread: true },
          },
        })
        .then(({ data }) => data),
    {
      refetchOnWindowFocus: false,
      getNextPageParam: ({ meta }) => {
        return meta?.last_page > meta?.current_page && meta?.current_page + 1
      },
    }
  )
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown>(
    () => api.get("suite/communication/notifications/read-all"),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
        ])
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
          "unread",
        ])
      },
    }
  )
}

export function useMarkAllAsUnread() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown>(
    () => api.get("suite/communication/notifications/unread-all"),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
        ])
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
          "unread",
        ])
      },
    }
  )
}

export function useMarkAsRead() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, string>(
    (notificationId) =>
      api.get(`suite/communication/notifications/read/${notificationId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
        ])
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
          "unread",
        ])
      },
    }
  )
}

export function useMarkAsUnread() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, string>(
    (notificationId) =>
      api.get(`suite/communication/notifications/unread/${notificationId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
        ])
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
          "unread",
        ])
      },
    }
  )
}

export function useDeleteNotification() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, string>(
    (notificationId) =>
      api.delete(`suite/communication/notifications/${notificationId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
        ])
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
          "unread",
        ])
      },
    }
  )
}

export function useEditConfiguration() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, object>(
    (data) => api.post(`suite/communication/notifications/configuration`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
        ])
        queryClient.invalidateQueries([
          "suite",
          "communication",
          "notifications",
          "unread",
        ])
      },
    }
  )
}
